import { Component } from "@theme/component";
import {
	fetchConfig,
	debounce,
	onAnimationEnd,
	prefersReducedMotion,
	resetShimmer,
} from "@theme/utilities";
import { morphSection, sectionRenderer } from "@theme/section-renderer";
import {
	ThemeEvents,
	CartUpdateEvent,
	QuantitySelectorUpdateEvent,
	CartAddEvent,
	DiscountUpdateEvent,
} from "@theme/events";
import { cartPerformance } from "@theme/performance";

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
	#debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

	connectedCallback() {
		super.connectedCallback();

		document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
		document.addEventListener(
			ThemeEvents.discountUpdate,
			this.handleDiscountUpdate,
		);
		document.addEventListener(
			ThemeEvents.quantitySelectorUpdate,
			this.#debouncedOnChange,
		);

		// Validate inventory on load
		this.#validateCartInventory();
	}

	disconnectedCallback() {
		super.disconnectedCallback();

		document.removeEventListener(
			ThemeEvents.cartUpdate,
			this.#handleCartUpdate,
		);
		document.removeEventListener(
			ThemeEvents.quantitySelectorUpdate,
			this.#debouncedOnChange,
		);
	}

	/**
	 * Handles QuantitySelectorUpdateEvent change event.
	 * @param {QuantitySelectorUpdateEvent} event - The event.
	 */
	#onQuantityChange(event) {
		const { quantity, cartLine: line } = event.detail;

		if (!line) return;

		if (quantity === 0) {
			return this.onLineItemRemove(line);
		}

		this.updateQuantity({
			line,
			quantity,
			action: "change",
		});
		const lineItemRow = this.refs.cartItemRows[line - 1];

		if (!lineItemRow) return;

		const textComponent = /** @type {TextComponent | undefined} */ (
			lineItemRow.querySelector("text-component")
		);
		textComponent?.shimmer();
	}

	/**
	 * Handles the line item removal.
	 * @param {number} line - The line item index.
	 */
	onLineItemRemove(line) {
		this.updateQuantity({
			line,
			quantity: 0,
			action: "clear",
		});

		const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

		if (!cartItemRowToRemove) return;

		const remove = () => cartItemRowToRemove.remove();

		if (prefersReducedMotion()) return remove();

		// Add class to the row to trigger the animation
		cartItemRowToRemove.style.setProperty(
			"--row-height",
			`${cartItemRowToRemove.clientHeight}px`,
		);
		cartItemRowToRemove.classList.add("removing");

		// Remove the row after the animation ends
		onAnimationEnd(cartItemRowToRemove, remove);
	}

	/**
	 * Updates the quantity.
	 * @param {Object} config - The config.
	 * @param {number} config.line - The line.
	 * @param {number} config.quantity - The quantity.
	 * @param {string} config.action - The action.
	 */
	updateQuantity(config) {
		const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(
			`${config.action}:user-action`,
		);

		this.#disableCartItems();

		const { line, quantity } = config;
		const { cartTotal } = this.refs;

		const cartItemsComponents = document.querySelectorAll(
			"cart-items-component",
		);
		const sectionsToUpdate = new Set([this.sectionId]);
		cartItemsComponents.forEach((item) => {
			if (item instanceof HTMLElement && item.dataset.sectionId) {
				sectionsToUpdate.add(item.dataset.sectionId);
			}
		});

		const body = JSON.stringify({
			line: line,
			quantity: quantity,
			sections: Array.from(sectionsToUpdate).join(","),
		});

		cartTotal?.shimmer();

		fetch(`${Theme.routes.cart_change_url}`, fetchConfig("json", { body }))
			.then((response) => {
				return response.text();
			})
			.then((responseText) => {
				const parsedResponseText = JSON.parse(responseText);

				resetShimmer(this);

				if (parsedResponseText.errors) {
					this.#handleCartError(line, parsedResponseText);
					return;
				}

				const newSectionHTML = new DOMParser().parseFromString(
					parsedResponseText.sections[this.sectionId],
					"text/html",
				);

				// Grab the new cart item count from a hidden element
				const newCartHiddenItemCount = newSectionHTML.querySelector(
					'[ref="cartItemCount"]',
				)?.textContent;
				const newCartItemCount = newCartHiddenItemCount
					? parseInt(newCartHiddenItemCount, 10)
					: 0;

				this.dispatchEvent(
					new CartUpdateEvent({}, this.sectionId, {
						itemCount: newCartItemCount,
						source: "cart-items-component",
						sections: parsedResponseText.sections,
					}),
				);

				morphSection(
					this.sectionId,
					parsedResponseText.sections[this.sectionId],
				);
			})
			.catch((error) => {
				console.error(error);
			})
			.finally(() => {
				this.#enableCartItems();
				cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
			});
	}

	/**
	 * Handles the discount update.
	 * @param {DiscountUpdateEvent} event - The event.
	 */
	handleDiscountUpdate = (event) => {
		this.#handleCartUpdate(event);
	};

	/**
	 * Handles the cart error.
	 * @param {number} line - The line.
	 * @param {Object} parsedResponseText - The parsed response text.
	 * @param {string} parsedResponseText.errors - The errors.
	 */
	#handleCartError = (line, parsedResponseText) => {
		const quantitySelector = this.refs.quantitySelectors[line - 1];
		const quantityInput = quantitySelector?.querySelector("input");

		if (!quantityInput) throw new Error("Quantity input not found");

		quantityInput.value = quantityInput.defaultValue;

		const cartItemError = this.refs[`cartItemError-${line}`];
		const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

		if (!(cartItemError instanceof HTMLElement))
			throw new Error("Cart item error not found");
		if (!(cartItemErrorContainer instanceof HTMLElement))
			throw new Error("Cart item error container not found");

		cartItemError.textContent = parsedResponseText.errors;
		cartItemErrorContainer.classList.remove("hidden");
	};

	/**
	 * Handles the cart update.
	 *
	 * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
	 */
	#handleCartUpdate = (event) => {
		if (event instanceof DiscountUpdateEvent) {
			sectionRenderer.renderSection(this.sectionId, { cache: false });
			return;
		}
		if (event.target === this) return;

		const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
		if (cartItemsHtml) {
			morphSection(this.sectionId, cartItemsHtml);
		} else {
			sectionRenderer.renderSection(this.sectionId, { cache: false });
		}
	};

	/**
	 * Disables the cart items.
	 */
	#disableCartItems() {
		this.classList.add("cart-items-disabled");
	}

	/**
	 * Enables the cart items.
	 */
	#enableCartItems() {
		this.classList.remove("cart-items-disabled");
	}

	/**
	 * Validates cart inventory on page load
	 */
	#validateCartInventory() {

		// Get cart data from the current page
		const cartForm = document.getElementById("cart-form");
		if (!cartForm) return;

		const quantityInputs = [
			...cartForm.querySelectorAll('input[name="updates[]"]'),
		];
		const itemsToUpdate = [];

		quantityInputs.forEach((input, index) => {
			const currentQuantity = parseInt(input.value);
			const maxQuantity = parseInt(input.getAttribute("max")) || Infinity;
			const cartLine = parseInt(input.dataset.cartLine);

			// Check if current quantity exceeds available inventory
			if (maxQuantity !== Infinity && currentQuantity > maxQuantity) {
				itemsToUpdate.push({ line: cartLine, currentQuantity, maxQuantity });
			}
		});

		// Update items that exceed inventory
		if (itemsToUpdate.length > 0) {
			this.#updateExcessiveQuantities(itemsToUpdate);
		}
	}

	/**
	 * Updates quantities that exceed inventory limits
	 * @param {Array} itemsToUpdate - Array of items that need quantity adjustment
	 */
	#updateExcessiveQuantities(itemsToUpdate) {

		// Show notification about inventory adjustments
		this.#showInventoryNotification(itemsToUpdate);

		// Update each item's quantity to the maximum available
		itemsToUpdate.forEach(({ line, maxQuantity }) => {
			this.updateQuantity({
				line,
				quantity: maxQuantity,
				action: "inventory-adjustment",
			});
		});
	}

	/**
	 * Shows notification about inventory adjustments
	 * @param {Array} itemsToUpdate - Array of items that were adjusted
	 */
	#showInventoryNotification(itemsToUpdate) {
		const message =
			itemsToUpdate.length === 1
				? `We've adjusted the quantity of one item in your cart to match available inventory.`
				: `We've adjusted the quantities of ${itemsToUpdate.length} items in your cart to match available inventory.`;

		this.#createToastNotification(message, "warning");
	}

	/**
	 * Creates a toast notification
	 * @param {string} message - The notification message
	 * @param {string} type - The notification type ('success', 'warning', 'error')
	 */
	#createToastNotification(message, type = "info") {
		// Remove existing toast if any
		const existingToast = document.querySelector(".inventory-toast");
		if (existingToast) {
			existingToast.remove();
		}

		// Create new toast
		const toast = document.createElement("div");
		toast.className = `inventory-toast inventory-toast--${type}`;
		toast.innerHTML = `
      <div class="inventory-toast__content">
        <span class="inventory-toast__message">${message}</span>
        <button class="inventory-toast__close" aria-label="Close">&times;</button>
      </div>
    `;

		// Add styles
		const style = document.createElement("style");
		style.textContent = `
      .inventory-toast {
        position: fixed;
        top: 20px;
        right: 20px;
        z-index: 1000;
        background: #F3F6EF;
        border: 1px solid #BCC1BB;
        border-radius: 6px;
        padding: 16px;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        max-width: 400px;
        opacity: 0;
        transform: translateY(-10px);
        transition: all 0.3s ease;
      }
      
      .inventory-toast--warning {
        border-color: #f59e0b;
        background: #fef3c7;
      }
      
      .inventory-toast--error {
        border-color: #ef4444;
        background: #fef2f2;
      }
      
      .inventory-toast.show {
        opacity: 1;
        transform: translateY(0);
      }
      
      .inventory-toast__content {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        gap: 12px;
      }
      
      .inventory-toast__message {
        font-family: 'PP Telegraf', sans-serif;
        font-size: 14px;
        line-height: 1.4;
        color: #121717;
        flex: 1;
      }
      
      .inventory-toast__close {
        background: none;
        border: none;
        cursor: pointer;
        padding: 0;
        color: #121717;
        opacity: 0.7;
        transition: opacity 0.2s ease;
        flex-shrink: 0;
        font-size: 18px;
        line-height: 1;
      }
      
      .inventory-toast__close:hover {
        opacity: 1;
      }
      
      @media (max-width: 768px) {
        .inventory-toast {
          right: 16px;
          top: 16px;
          max-width: calc(100vw - 32px);
        }
      }
    `;

		// Add style if it doesn't exist
		if (!document.querySelector("style[data-inventory-toast]")) {
			style.setAttribute("data-inventory-toast", "");
			document.head.appendChild(style);
		}

		// Add toast to body
		document.body.appendChild(toast);

		// Show toast with animation
		setTimeout(() => {
			toast.classList.add("show");
		}, 10);

		// Handle close button
		const closeBtn = toast.querySelector(".inventory-toast__close");
		closeBtn?.addEventListener("click", () => {
			toast.classList.remove("show");
			setTimeout(() => {
				toast.remove();
			}, 300);
		});

		// Auto-hide after 8 seconds
		setTimeout(() => {
			if (toast.parentNode) {
				toast.classList.remove("show");
				setTimeout(() => {
					toast.remove();
				}, 300);
			}
		}, 8000);
	}

	/**
	 * Gets the section id.
	 * @returns {string} The section id.
	 */
	get sectionId() {
		const { sectionId } = this.dataset;

		if (!sectionId) throw new Error("Section id missing");

		return sectionId;
	}
}

if (!customElements.get("cart-items-component")) {
	customElements.define("cart-items-component", CartItemsComponent);
}
